<div class="permissions-container">
  <div class="header-section">
    <h1>User Permissions Management</h1>
    <p class="subtitle">Assign roles and entity access permissions to users</p>
  </div>

  @if (userRole === 'Viewer') {
    <mat-card class="warning-card">
      <mat-card-content>
        <div class="warning-content">
          <mat-icon>warning</mat-icon>
          <div>
            <h3>Viewer Permissions Only</h3>
            <p>You only have VIEWER permissions. If you believe you should have more permissions, please email helpdesk@businessdataapps.com</p>
          </div>
        </div>
      </mat-card-content>
    </mat-card>
  }

  @if (!canManageUsers) {
    <mat-card class="warning-card">
      <mat-card-content>
        <div class="warning-content">
          <mat-icon>block</mat-icon>
          <div>
            <h3>Insufficient Permissions</h3>
            <p>You don't have the right role to manage roles and permissions. Access
               is limited to the Administrator and System Administrator roles. Contact
               an administrator if you need access.</p>
          </div>
        </div>
      </mat-card-content>
    </mat-card>
  }

  @if (canManageUsers) {
    <div class="main-content">
      <!-- User Selection Card -->
      <mat-card class="selection-card">
        <mat-card-header>
          <mat-card-title>Select User</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Select a user to manage permissions</mat-label>
            <mat-select [formControl]="selectedUserControl">
              @for (user of listOfUsers(); track user.email) {
                <mat-option [value]="user">
                  {{ getUserDisplayName(user) }}
                </mat-option>
              }
            </mat-select>
            <mat-icon matSuffix>person</mat-icon>
          </mat-form-field>

          @if (selectedUserControl.value) {
            <div class="user-info-section">
              <mat-divider></mat-divider>
              <div class="user-info">
                <div class="info-row">
                  <span class="info-label">First Name:</span>
                  <span class="info-value">{{ selectedUserControl.value.given_name }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Last Name:</span>
                  <span class="info-value">{{ selectedUserControl.value.family_name }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Email:</span>
                  <span class="info-value">{{ selectedUserControl.value.email }}</span>
                </div>
              </div>

              <mat-divider></mat-divider>
              <div class="contract-counts-section">
                @if (calculatingCount()) {
                  <div class="calculating-spinner">
                    <mat-spinner diameter="40"></mat-spinner>
                    <span class="calculating-text">Updating...</span>
                  </div>
                } @else {
                  <div class="count-display">
                    <span class="count-label">Number of Contracts Visible to this User:</span>
                    @if (visibleContractsCount() === 0) {
                      <div class="warning-section">
                        <span class="warning-text">Warning!</span>
                      </div>
                    }
                    <span class="count-value-large" [class.zero-contracts]="visibleContractsCount() === 0">
                      {{ visibleContractsCount() }}
                    </span>
                  </div>
                }
              </div>
            </div>
          }
        </mat-card-content>
      </mat-card>

      <!-- Save Button (Top) -->
      @if (selectedUserControl.value) {
        <div class="action-section">
          <button
            mat-raised-button
            color="primary"
            (click)="saveUserPermissions()"
            [disabled]="saving">
            <mat-icon>save</mat-icon>
            {{ saving ? 'Saving...' : 'Save User Permissions' }}
          </button>
        </div>
      }

      <!-- Permissions Configuration (Only show when user is selected) -->
      @if (selectedUserControl.value) {
        <div class="permissions-section">

          <!-- Role Selection -->
          <mat-card class="config-card">
            <mat-card-header>
              <mat-card-title>
                <mat-icon>badge</mat-icon>
                User Role
              </mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Select Role</mat-label>
                <mat-select [formControl]="selectedRoleControl">
                  @for (role of roleList(); track role) {
                    <mat-option [value]="role">{{ role }}</mat-option>
                  }
                </mat-select>
                <mat-icon matSuffix>security</mat-icon>
              </mat-form-field>
              <div class="help-text">
                <mat-icon class="info-icon">info</mat-icon>
                <span>Users with "Viewer" role will not be able to access Edit or New buttons throughout the application.</span>
              </div>
            </mat-card-content>
          </mat-card>

          <!-- Departments -->
          <mat-card class="config-card">
            <mat-card-header>
              <mat-card-title>
                <mat-icon>business</mat-icon>
                Departments
              </mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Search and select departments</mat-label>
                <input
                  type="text"
                  matInput
                  [formControl]="departmentInputControl"
                  [matAutocomplete]="autoDepartments">
                <mat-icon matSuffix>apartment</mat-icon>
                <mat-autocomplete #autoDepartments="matAutocomplete" (optionSelected)="selectDepartment($event.option.value)">
                  @for (department of filteredDepartments$ | async; track department) {
                    <mat-option [value]="department">{{ department }}</mat-option>
                  }
                </mat-autocomplete>
              </mat-form-field>
              @if (selectedDepartmentsControl.value && selectedDepartmentsControl.value.length > 0) {
                <div class="chips-container">
                  <mat-chip-set>
                    @for (dept of selectedDepartmentsControl.value; track dept) {
                      <mat-chip (removed)="removeDepartment(dept)">
                        {{ dept }}
                        <button matChipRemove>
                          <mat-icon>cancel</mat-icon>
                        </button>
                      </mat-chip>
                    }
                  </mat-chip-set>
                </div>
              }
            </mat-card-content>
          </mat-card>

          <!-- Sites -->
          <mat-card class="config-card">
            <mat-card-header>
              <mat-card-title>
                <mat-icon>location_on</mat-icon>
                Sites
              </mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Search and select sites</mat-label>
                <input
                  type="text"
                  matInput
                  [formControl]="siteInputControl"
                  [matAutocomplete]="autoSites">
                <mat-icon matSuffix>place</mat-icon>
                <mat-autocomplete #autoSites="matAutocomplete" (optionSelected)="selectSite($event.option.value)" [displayWith]="getSiteDisplayName">
                  @for (site of filteredSites$ | async; track site.siteNum) {
                    <mat-option [value]="site">{{ getSiteDisplayName(site) }}</mat-option>
                  }
                </mat-autocomplete>
              </mat-form-field>
              @if (selectedSitesControl.value && selectedSitesControl.value.length > 0) {
                <div class="chips-container">
                  <mat-chip-set>
                    @for (site of selectedSitesControl.value; track site.siteNum) {
                      <mat-chip (removed)="removeSite(site)">
                        {{ getSiteDisplayName(site) }}
                        <button matChipRemove>
                          <mat-icon>cancel</mat-icon>
                        </button>
                      </mat-chip>
                    }
                  </mat-chip-set>
                </div>
              }
            </mat-card-content>
          </mat-card>

          <!-- Legal Entities -->
          <mat-card class="config-card">
            <mat-card-header>
              <mat-card-title>
                <mat-icon>account_balance</mat-icon>
                Legal Entities
              </mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Search and select legal entities</mat-label>
                <input
                  type="text"
                  matInput
                  [formControl]="legalEntityInputControl"
                  [matAutocomplete]="autoLegalEntities">
                <mat-icon matSuffix>domain</mat-icon>
                <mat-autocomplete #autoLegalEntities="matAutocomplete" (optionSelected)="selectLegalEntity($event.option.value.legalEntityNum)" [displayWith]="getLegalEntityDisplayName">
                  @for (entity of filteredLegalEntities$ | async; track entity.legalEntityNum) {
                    <mat-option [value]="entity">{{ getLegalEntityDisplayName(entity) }}</mat-option>
                  }
                </mat-autocomplete>
              </mat-form-field>
              @if (selectedLegalEntitiesControl.value && selectedLegalEntitiesControl.value.length > 0) {
                <div class="chips-container">
                  <mat-chip-set>
                    @for (entityNum of selectedLegalEntitiesControl.value; track entityNum) {
                      <mat-chip (removed)="removeLegalEntity(entityNum)">
                        @for (entity of allLegalEntities(); track entity.legalEntityNum) {
                          @if (entity.legalEntityNum === entityNum) {
                            {{ getLegalEntityDisplayName(entity) }}
                          }
                        }
                        <button matChipRemove>
                          <mat-icon>cancel</mat-icon>
                        </button>
                      </mat-chip>
                    }
                  </mat-chip-set>
                </div>
              }
            </mat-card-content>
          </mat-card>

          <!-- Contract Types -->
          <mat-card class="config-card">
            <mat-card-header>
              <mat-card-title>
                <mat-icon>description</mat-icon>
                Contract Types
              </mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Search and select contract types</mat-label>
                <input
                  type="text"
                  matInput
                  [formControl]="contractTypeInputControl"
                  [matAutocomplete]="autoContractTypes">
                <mat-icon matSuffix>contract</mat-icon>
                <mat-autocomplete #autoContractTypes="matAutocomplete" (optionSelected)="selectContractType($event.option.value)">
                  @for (type of filteredContractTypes$ | async; track type) {
                    <mat-option [value]="type">{{ type }}</mat-option>
                  }
                </mat-autocomplete>
              </mat-form-field>
              @if (selectedContractTypesControl.value && selectedContractTypesControl.value.length > 0) {
                <div class="chips-container">
                  <mat-chip-set>
                    @for (type of selectedContractTypesControl.value; track type) {
                      <mat-chip (removed)="removeContractType(type)">
                        {{ type }}
                        <button matChipRemove>
                          <mat-icon>cancel</mat-icon>
                        </button>
                      </mat-chip>
                    }
                  </mat-chip-set>
                </div>
              }
            </mat-card-content>
          </mat-card>

          <!-- Database Organizations -->
          <mat-card class="config-card">
            <mat-card-header>
              <mat-card-title>
                <mat-icon>storage</mat-icon>
                Database Organizations
              </mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Search and select database organizations</mat-label>
                <input
                  type="text"
                  matInput
                  [formControl]="databaseOrgInputControl"
                  [matAutocomplete]="autoDatabaseOrgs">
                <mat-icon matSuffix>corporate_fare</mat-icon>
                <mat-autocomplete #autoDatabaseOrgs="matAutocomplete" (optionSelected)="selectDatabaseOrg($event.option.value)">
                  @for (org of filteredDatabaseOrgs$ | async; track org) {
                    <mat-option [value]="org">{{ org }}</mat-option>
                  }
                </mat-autocomplete>
              </mat-form-field>
              @if (selectedDatabaseOrganizationsControl.value && selectedDatabaseOrganizationsControl.value.length > 0) {
                <div class="chips-container">
                  <mat-chip-set>
                    @for (org of selectedDatabaseOrganizationsControl.value; track org) {
                      <mat-chip (removed)="removeDatabaseOrg(org)">
                        {{ org }}
                        <button matChipRemove>
                          <mat-icon>cancel</mat-icon>
                        </button>
                      </mat-chip>
                    }
                  </mat-chip-set>
                </div>
              }
            </mat-card-content>
          </mat-card>

        </div>

        <!-- Save Button (Bottom) -->
        <div class="action-section bottom-action">
          <button
            mat-raised-button
            color="primary"
            (click)="saveUserPermissions()"
            [disabled]="saving">
            <mat-icon>save</mat-icon>
            {{ saving ? 'Saving...' : 'Save User Permissions' }}
          </button>
        </div>
      }
    </div>
  }
</div>
